import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponseHeaders } from "axios";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useExportOutreachReport,
  useGetOutreachReports,
} from "../../../../../api/services/reports/reportsApi";
import { MuiBox } from "../../../../../components";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { getFileNameFromHeader } from "../../../helper";
import { OutreachReportFormKeys } from "../../../modelsAndSchemas/models";
import { OutreachReportFormSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { OutreachReportForm } from "../outreachReportForm/OutreachReportForm";
import { OutreachReportResult } from "../outreachReportResult/OutreachReportResult";
import { OutreachReportSummary } from "../../../../../api/services/reports/models";

export const OutreachReportSearch = () => {
  const {
    commonValidationMessages: { requiredValidation },
    commonLabels: { someThingWentWrongPleaseTryAgainLater },
  } = useSiteCore();
  const methods = useForm<OutreachReportFormKeys>({
    defaultValues: {
      marketOfDeliveryOffices: [],
    },
    resolver: yupResolver(OutreachReportFormSchema(requiredValidation)),
  });
  const [outReachList, setOutReachList] = useState<OutreachReportSummary[]>([]);

  const {
    mutateAsync: getOutreachReports,
    data: outreachReportList,
    isPending: isOutReachReportLoading,
    isSuccess: isGetOutReachReportSuccess,
    isError: isGetOutReachReportError,
    isFiltered: isOutReachReportFiltered,
    filterParams: outreachReportListFilteredParams,
  } = useGetOutreachReports();

  const {
    mutateAsync: exportOutreachReport,
    isPending: isExportOutreachReportLoading,
    isError: isExportOutreachReportError,
  } = useExportOutreachReport();

  const dispatch = useDispatch();
  const pendingBillingSearchFormRef = useRef<{ reset: () => void }>(null);

  const isNotificationStatusApiLoading =
    isOutReachReportLoading || isExportOutreachReportLoading;
  const isNotificationStatusApiError =
    isGetOutReachReportError || isExportOutreachReportError;

  useEffect(() => {
    if (isNotificationStatusApiLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isNotificationStatusApiLoading]);

  useEffect(() => {
    if (isGetOutReachReportSuccess) {
      setOutReachList(outreachReportList?.data ?? []);
    }
  }, [isGetOutReachReportSuccess, outreachReportList]);

  useEffect(() => {
    if (isNotificationStatusApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isNotificationStatusApiError]);

  const { handleSubmit } = methods;
  const handleSearchFormSubmit = (data: OutreachReportFormKeys) => {
    getOutreachReports({
      marketIds: data.marketOfDeliveryOffices,
    });
  };

  const handleExport = async () => {
    const response = await exportOutreachReport(
      outreachReportListFilteredParams,
    );
    const filename = getFileNameFromHeader(
      response.headers as AxiosResponseHeaders,
    );
    saveAs(response.data, filename);
  };

  return (
    <MuiBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSearchFormSubmit)}>
          <OutreachReportForm
            ref={pendingBillingSearchFormRef}
            getOutreachReports={getOutreachReports}
          />
        </form>
      </FormProvider>
      <OutreachReportResult
        onExport={handleExport}
        isFilteredOutReachReport={isOutReachReportFiltered}
        outReachReports={outReachList}
      />
    </MuiBox>
  );
};
